import React, { useState, useEffect, useMemo } from 'react';
import { dataApi, listCustomerOrganisations } from '../../services/api';
import { toast } from 'sonner';
import PageHeaderActions from '../../components/PageHeaderActions';
import ListToolbar from '../../components/ListToolbar';
import { useObfuscate } from '../../hooks/useObfuscate';
import { cn } from '../../lib/utils';

// shadcn UI components
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { SegmentedControl } from '../../components/ui/segmented';
import { Skeleton } from '../../components/ui/skeleton';
import CustomerForm from '../overlays/CustomerForm';
import CustomerOrganisationForm, { ROLE_OPTIONS } from '../overlays/CustomerOrganisationForm';
import { ChevronRight, Phone, Mail, Building2, Pencil, Plus } from 'lucide-react';

const SORT_OPTIONS = [
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'name-desc', label: 'Name (Z–A)' },
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' }
];

const ROLE_FILTER_OPTIONS = [{ value: 'all', label: 'Any role' }, ...ROLE_OPTIONS];

const DEFAULT_LIST_VIEW = { sort: 'name', role: 'all' };

// Sort and filter are remembered between visits; the search is not.
const loadListView = () => {
  try {
    return { ...DEFAULT_LIST_VIEW, ...JSON.parse(localStorage.getItem('phonebookListView') || '{}') };
  } catch {
    return DEFAULT_LIST_VIEW;
  }
};

const contactName = (contact) => `${contact.first_name || ''} ${contact.last_name || ''}`.trim();

// A person in the phonebook is a customer. An organisation carries its roles
// as a comma-separated list.
const contactRoles = (contact) =>
  contact.type === 'customer'
    ? ['customer']
    : (contact.roles || '').split(',').map((role) => role.trim()).filter(Boolean);

const Phonebook = () => {
  const priv = useObfuscate();

  // Only the type is read back. Older caches also hold a search, from when the
  // search box sat in the filter bar and was remembered with it.
  const loadCachedFilters = () => {
    try {
      const cached = JSON.parse(localStorage.getItem('phonebookFilters') || '{}');
      return { type: cached.type || 'all' };
    } catch {
      return { type: 'all' };
    }
  };

  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [filters, setFilters] = useState(loadCachedFilters());
  const [search, setSearch] = useState('');
  const [listView, setListView] = useState(loadListView);
  const [customerFormOpen, setCustomerFormOpen] = useState(false);
  const [orgFormOpen, setOrgFormOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [relatedQuotes, setRelatedQuotes] = useState([]);
  const [relatedLoading, setRelatedLoading] = useState(false);

  const typeFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'customer', label: 'Customers' },
    { value: 'organization', label: 'Organisations' }
  ];

  const fetchContacts = async () => {
    try {
      if (initialLoading) {
        setInitialLoading(true);
      } else {
        setListLoading(true);
      }
      
      const results = [];

      // Fetch customers if filter is 'all' or 'customer'
      if (filters.type === 'all' || filters.type === 'customer') {
        // Searching is done on the loaded list, so nothing is passed here.
        const customers = await dataApi.customerList();
        const customerContacts = customers.map(customer => ({
          id: customer.$id,
          type: 'customer',
          first_name: customer.firstName || customer.first_name,
          last_name: customer.lastName || customer.last_name,
          phone: customer.phone,
          email: customer.email || '',
          street1: customer.street1,
          street2: customer.street2,
          suburb: customer.suburb,
          region: customer.region,
          postcode: customer.postcode,
          created_at: customer.$createdAt,
          quote_count: customer.quote_count || 0,
          customer_organisation_name: customer.customerOrganisation?.name || customer.customer_organisation?.name || null
        }));
        results.push(...customerContacts);
      }

      // Fetch organisations if filter is 'all' or 'organization'
      if (filters.type === 'all' || filters.type === 'organization') {
        try {
          const orgsResponse = await listCustomerOrganisations({ status: 'active' });
          const orgContacts = (orgsResponse.documents || []).map(org => {
            const phones = (() => {
              try { return JSON.parse(org.phones || '[]'); } catch { return []; }
            })();
            const emails = (() => {
              try { return JSON.parse(org.emails || '[]'); } catch { return []; }
            })();
            return {
              id: org.$id,
              type: 'organization',
              first_name: org.name,
              last_name: '',
              phone: phones[0]?.number || '',
              email: emails[0]?.email || '',
              street1: org.street1,
              street2: org.street2,
              suburb: org.suburb,
              region: org.region,
              postcode: org.postcode,
              created_at: org.$createdAt,
              roles: org.roles,
              tradingName: org.tradingName,
              _raw: org
            };
          });
          results.push(...orgContacts);
        } catch (err) {
          console.error('Error fetching organisations:', err);
        }
      }
      
      setContacts(results);

      // Keep the open contact's details current, e.g. after an edit. With
      // nothing open, the effect below picks the top of the list.
      setSelectedContact((current) =>
        current ? results.find((contact) => contact.id === current.id) || current : current
      );
    } catch (error) {
      showErrorToast('Failed to fetch contacts');
      console.error('Error fetching contacts:', error);
    } finally {
      setInitialLoading(false);
      setListLoading(false);
    }
  };

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
  };

  // Helper functions for toast messages
  const showSuccessToast = (message) => {
    toast.success(message);
  };

  const showErrorToast = (message) => {
    toast.error(message);
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (field, value) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    setFilters(newFilters);
    localStorage.setItem('phonebookFilters', JSON.stringify(newFilters));
  };

  const updateListView = (patch) => {
    const next = { ...listView, ...patch };
    setListView(next);
    localStorage.setItem('phonebookListView', JSON.stringify(next));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const buildAddressLines = (contact) => {
    // Dev mode: addresses are blanked out
    if (priv.active) return ['—'];
    const lines = [];
    if (contact.street1) lines.push(contact.street1);
    if (contact.street2) lines.push(contact.street2);
    const suburbRegion = [contact.suburb, contact.region].filter(Boolean).join(', ');
    const finalLine = [suburbRegion, contact.postcode].filter(Boolean).join(' ').trim();
    if (finalLine) lines.push(finalLine);
    return lines.length ? lines : ['Address not provided'];
  };

  const formatRequestNumber = (quote) => {
    const raw = quote?.quoteRequestNumber || quote?.quote_request_number || quote?.requestNumber || quote?.request_number || quote?.$sequence;
    const numeric = parseInt(raw, 10);
    const padded = Number.isFinite(numeric) ? String(Math.max(0, numeric)).padStart(5, '0') : String(raw || '0').padStart(5, '0');
    return `QR-${padded}`;
  };

  useEffect(() => {
    const loadRelatedQuotes = async () => {
      if (!selectedContact?.id) {
        setRelatedQuotes([]);
        return;
      }

      try {
        setRelatedLoading(true);
        const quotes = await dataApi.customerQuoteRequests(selectedContact.id);
        setRelatedQuotes(quotes || []);
      } catch (error) {
        showErrorToast('Failed to load related quote requests');
        console.error('Error loading related quotes:', error);
      } finally {
        setRelatedLoading(false);
      }
    };

    loadRelatedQuotes();
  }, [selectedContact?.id]);

  // The list as shown: narrowed by role and search, then sorted. Phone and
  // email are searchable even though the list no longer shows them.
  const filteredContacts = useMemo(() => {
    const term = search.trim().toLowerCase();

    const rows = contacts.filter((contact) => {
      if (listView.role !== 'all' && !contactRoles(contact).includes(listView.role)) return false;
      if (!term) return true;
      return [contactName(contact), contact.tradingName, contact.phone, contact.email]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term));
    });

    const byName = (a, b) => contactName(a).localeCompare(contactName(b), undefined, { sensitivity: 'base' });
    const byCreated = (a, b) => new Date(a.created_at) - new Date(b.created_at);

    switch (listView.sort) {
      case 'name-desc':
        return [...rows].sort((a, b) => byName(b, a));
      case 'newest':
        return [...rows].sort((a, b) => byCreated(b, a));
      case 'oldest':
        return [...rows].sort(byCreated);
      default:
        return [...rows].sort(byName);
    }
  }, [contacts, search, listView]);

  const listNarrowed = Boolean(search.trim()) || listView.role !== DEFAULT_LIST_VIEW.role;

  useEffect(() => {
    if (!selectedContact && filteredContacts.length > 0) {
      setSelectedContact(filteredContacts[0]);
    }
  }, [selectedContact, filteredContacts]);

  const listSkeleton = (count) => (
    <div className="space-y-2">
      {[...Array(count)].map((_, i) => (
        <Card key={i} className="p-4">
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-20" />
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col h-full p-6 gap-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold">Phonebook</h1>
        <PageHeaderActions />
      </div>

      {/* Type on the left, actions on the right — the same row as the Fleet
          page's tabs. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          options={typeFilterOptions}
          value={filters.type}
          onChange={(value) => handleFilterChange('type', value)}
        />
        <Button
          size="sm"
          onClick={() => showSuccessToast('Add contact feature coming soon!')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add contact
        </Button>
      </div>

      {initialLoading ? (
        <div className="grid grid-cols-1 xl:grid-cols-8 gap-4 flex-1 min-h-0">
          <Card className="xl:col-span-3 min-h-0 p-4 space-y-4">
            <Skeleton className="h-9 w-full" />
            {listSkeleton(5)}
          </Card>

          <Card className="xl:col-span-5 min-h-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <Skeleton className="h-6 w-32" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <Skeleton className="h-5 w-40 mb-3" />
                  <div className="space-y-3">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="flex justify-between">
                        <Skeleton className="h-4 w-16" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Skeleton className="h-5 w-24 mb-3" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4 mt-1" />
                </div>
                <div>
                  <Skeleton className="h-5 w-32 mb-3" />
                  <div className="space-y-2">
                    {[...Array(2)].map((_, i) => (
                      <div key={i} className="flex justify-between">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-16" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
          <div className="grid grid-cols-1 xl:grid-cols-8 gap-4 flex-1 min-h-0">
            {/* Contacts List Panel — no heading of its own: the page title says
                what it is. */}
            <Card className="xl:col-span-3 flex flex-col min-h-0">
              <div className="flex-shrink-0 p-4 pb-2">
                <ListToolbar
                  search={search}
                  onSearchChange={setSearch}
                  placeholder="Search by name, phone, email…"
                  sortOptions={SORT_OPTIONS}
                  sort={listView.sort}
                  onSortChange={(sort) => updateListView({ sort })}
                  filterGroups={[
                    {
                      id: 'role',
                      label: 'Role',
                      options: ROLE_FILTER_OPTIONS,
                      value: listView.role,
                      defaultValue: DEFAULT_LIST_VIEW.role
                    }
                  ]}
                  onFilterChange={(id, value) => updateListView({ [id]: value })}
                />
              </div>

              <div className="flex-1 overflow-y-auto px-4 pt-1 pb-4">
                {listLoading && filteredContacts.length === 0 ? (
                  listSkeleton(5)
                ) : filteredContacts.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">No contacts found.</p>
                    {listNarrowed && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSearch('');
                          updateListView({ role: DEFAULT_LIST_VIEW.role });
                        }}
                      >
                        Clear search and filters
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {listLoading && (
                      <div className="text-xs text-muted-foreground">Updating contacts…</div>
                    )}
                    {filteredContacts.map((contact, index) => {
                      const name = contact.type === 'organization'
                        ? contactName(contact)
                        : priv.name(contactName(contact));
                      return (
                      <Card
                        key={contact.id || `${contact.first_name}_${contact.last_name}_${index}`}
                        className={cn(
                          'group cursor-pointer transition-colors hover:bg-accent/50',
                          selectedContact?.id === contact.id && 'ring-2 ring-primary'
                        )}
                        onClick={() => handleContactSelect(contact)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold flex items-center gap-1.5 min-w-0">
                                {contact.type === 'organization' && <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                                <span className="truncate">{name}</span>
                              </h3>
                              {contact.tradingName && (
                                <p className="text-xs text-muted-foreground truncate">t/a {contact.tradingName}</p>
                              )}
                              <p className="text-sm text-muted-foreground">
                                {contact.type === 'customer' ? 'Customer' : 'Organisation'}
                              </p>
                            </div>
                            {/* Shown on hover. Does what clicking the row does. */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 [&_svg]:size-5"
                              aria-label={`Show ${name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleContactSelect(contact);
                              }}
                            >
                              <ChevronRight />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>

            {/* Contact Preview Panel */}
            <Card className="xl:col-span-5 min-h-0">
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle>Contact Details</CardTitle>
                  {selectedContact && (
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={!selectedContact.phone}
                        onClick={() => {
                          if (!selectedContact.phone) return;
                          window.location.href = `tel:${selectedContact.phone}`;
                        }}
                      >
                        <Phone className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={!selectedContact.email}
                        onClick={() => {
                          if (!selectedContact.email) return;
                          window.location.href = `mailto:${selectedContact.email}`;
                        }}
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          if (selectedContact.type === 'organization') {
                            setSelectedOrgId(selectedContact.id);
                            setOrgFormOpen(true);
                          } else {
                            setCustomerFormOpen(true);
                          }
                        }}
                      >
                        <Pencil className="mr-1 h-4 w-4" /> Edit
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {selectedContact ? (
                  <div className="space-y-6">
                    {/* Contact Information */}
                    <div>
                      <h3 className="font-semibold mb-3">Contact Information</h3>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-28 text-muted-foreground">Name</span>
                          <span className="flex-1 font-medium">
                            {selectedContact.type === 'organization'
                              ? `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim()
                              : priv.name(`${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim())}
                          </span>
                        </div>
                        {selectedContact.phone && (
                          <div className="flex items-center gap-2">
                            <span className="w-28 text-muted-foreground">Phone</span>
                            <span className="flex-1 font-medium">{priv.phone(selectedContact.phone)}</span>
                          </div>
                        )}
                        {selectedContact.email && (
                          <div className="flex items-center gap-2">
                            <span className="w-28 text-muted-foreground">Email</span>
                            <span className="flex-1 font-medium">{priv.email(selectedContact.email, selectedContact.first_name)}</span>
                          </div>
                        )}
                        {selectedContact.customer_organisation_name && (
                          <div className="flex items-center gap-2">
                            <span className="w-28 text-muted-foreground">Organisation</span>
                            <span className="flex-1 font-medium">{selectedContact.customer_organisation_name}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Address Information */}
                    <div>
                      <h3 className="font-semibold mb-3">Address</h3>
                      <div className="text-sm whitespace-pre-line">
                        {buildAddressLines(selectedContact).join('\n')}
                      </div>
                    </div>

                    {/* Activity Summary */}
                    <div>
                      <h3 className="font-semibold mb-3">Related</h3>
                      <Card className="border">
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm">Quote Requests ({relatedLoading ? '...' : relatedQuotes.length})</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {relatedLoading ? (
                            <div className="space-y-2">
                              <Skeleton className="h-12 w-full" />
                              <Skeleton className="h-12 w-full" />
                              <Skeleton className="h-12 w-3/4" />
                            </div>
                          ) : relatedQuotes.length === 0 ? (
                            <div className="text-center py-6 text-muted-foreground">No quote requests found for this contact.</div>
                          ) : (
                            relatedQuotes.map((quote) => {
                              const requestNumber = formatRequestNumber(quote);
                              return (
                                <div key={quote.id} className="flex items-center justify-between p-3 border rounded-md">
                                  <div className="space-y-1">
                                    <div className="text-sm font-semibold flex items-center gap-2">
                                      <span>{requestNumber}</span>
                                      <span className="text-muted-foreground text-xs">{quote.status || 'Unknown'}</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground flex gap-3">
                                      <span>Created: {formatDate(quote.created_at || quote.$createdAt)}</span>
                                      <span>Updated: {formatDate(quote.updated_at || quote.$updatedAt)}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3 text-sm">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => window.open(`/quote-requests/${quote.id || quote.$id}`, '_blank')}
                                      aria-label="Open quote request"
                                    >
                                      <ChevronRight className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">Select a contact from the list to view details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
      )}

      <CustomerForm
        customerId={selectedContact?.id}
        isOpen={customerFormOpen}
        onClose={() => {
          setCustomerFormOpen(false);
          fetchContacts();
        }}
      />

      <CustomerOrganisationForm
        organisationId={selectedOrgId}
        isOpen={orgFormOpen}
        onClose={() => {
          setOrgFormOpen(false);
          setSelectedOrgId(null);
          fetchContacts();
        }}
        onSave={() => fetchContacts()}
      />

    </div>
  );
};

export default Phonebook;
